import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Award } from "lucide-react";
import { motion } from "framer-motion";

export default function About() {
  useEffect(() => {
    document.title = "About — OVI";
  }, []);

  return (
    <div className="bg-[var(--ovi-cream)]">
      {/* Header */}
      <section className="border-b border-[var(--ovi-border)] ovi-texture">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div className="flex justify-center mb-10 sm:mb-14">
            <img
              src="/ovi-makers-full-logo.png"
              alt="OVI Makers — The Sound of Making"
              className="w-[220px] sm:w-[280px] lg:w-[320px] h-auto"
              data-testid="about-brand-emblem"
            />
          </div>
          <p className="text-xs uppercase tracking-[0.24em] text-[var(--ovi-terracotta)] font-semibold mb-5 text-center">
            About OVI
          </p>
          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[var(--ovi-indigo)] tracking-tight leading-[1.05] text-center"
            data-testid="about-page-title"
          >
            We're not building a marketplace. We're amplifying a song.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed max-w-2xl mx-auto text-center">
            OVI is a direct-to-consumer textile brand from Solapur, Maharashtra.
            We connect buyers across India to the weavers, dyers and makers behind
            the cloth — with no warehouses, no middlemen, and no markup.
          </p>
        </div>
      </section>

      {/* Founding story + name + mission */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold">
            01 · The founding
          </p>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)] leading-[1.15]">
            Born in Solapur, built from within — for India.
          </h2>
          <div className="mt-6 space-y-5 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed">
            <p>
              OVI was born in Solapur — not discovered from the outside, but
              built from within. Named after the folk songs Marathi women sing
              while weaving, OVI exists because the makers behind India's
              finest textiles had no direct voice to the people buying their
              work.
            </p>
            <p>
              No brand. No platform. No name. Just craft, passed through hands
              for generations, sold through layers of middlemen who took the
              margin and left the story behind.
            </p>
            <p className="text-[var(--ovi-indigo)] font-medium">
              We are changing that — one maker at a time, one cluster at a time, across India.
            </p>
          </div>
        </motion.div>

        <Separator className="my-12 sm:my-16 bg-[var(--ovi-border)]" />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold">
            02 · The name
          </p>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)]">
            Why we're called OVI.
          </h2>
          <div className="mt-6 space-y-5 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed">
            <p>
              <span className="font-serif-display italic text-[var(--ovi-indigo)]">Ovi</span> (ओवी)
              is a form of Marathi folk verse traditionally sung by women at
              work — grinding grain at the stone mill, weaving on the loom, lulling
              a child to sleep. It is a song of labour. A song of memory. A song
              that turns repetitive work into something that carries meaning.
            </p>
            <p>
              We chose the name because every piece of cloth we sell carries that
              same sound — the rhythm of a human being making something with
              their hands, in a small town, over hours and hours of work.
            </p>
          </div>
        </motion.div>

        <Separator className="my-12 sm:my-16 bg-[var(--ovi-border)]" />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold">
            03 · The mission
          </p>
          <h2
            className="mt-3 font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)]"
            data-testid="about-page-mission"
          >
            Make Solapur a household name.
          </h2>
          <div className="mt-6 space-y-5 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed">
            <p>
              Our mission is simple: every Indian home should know where their
              cloth comes from — and the makers should earn fairly for it.
            </p>
            <p>
              We're starting with bed sheets, chaddars, terry towels and denim.
              Over time, we'll bring more of Solapur's craft online, build maker
              profiles, and tell more of the stories that usually stay hidden
              behind the cloth.
            </p>
          </div>
        </motion.div>
      </section>

      {/* WHY OVI EXISTS — deep indigo full-width band */}
      <section
        className="relative bg-[var(--ovi-indigo)] text-[var(--ovi-cream-2)] ovi-noise w-full"
        data-testid="about-why-ovi-exists"
      >
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
          <p className="text-xs uppercase tracking-[0.28em] text-[#E4B79A] font-semibold mb-6">
            Why OVI exists
          </p>
          <h2 className="font-serif-display text-[clamp(28px,4.4vw,56px)] leading-[1.15] tracking-tight text-[var(--ovi-cream-2)]">
            Solapur has 50,000 powerloom units and a textile output worth thousands of crores annually. But 80% of small manufacturers have zero digital presence, no direct buyer access, and are completely dependent on middlemen who take 30 to 40 percent margin.
          </h2>
          <p className="mt-8 font-serif-display text-[clamp(22px,3.2vw,40px)] leading-[1.2] text-[var(--ovi-terracotta)]">
            OVI is the platform that changes this.
          </p>
          <p className="mt-5 font-serif-display text-[clamp(22px,3.2vw,40px)] leading-[1.2] text-[var(--ovi-cream-2)]">
            We are not disrupting the established industry. We are rescuing the 80 percent it left behind.
          </p>
        </div>
      </section>

      {/* GI ADVANTAGE — cream band with indigo accent */}
      <section className="bg-[var(--ovi-cream-2)] border-y border-[var(--ovi-border)]" data-testid="about-gi-advantage">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--ovi-terracotta)] font-semibold mb-4">
                The GI advantage
              </p>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[var(--ovi-indigo)] leading-[1.1]">
                Authenticated Indian craft, protected by law.
              </h2>
              <div className="mt-6 space-y-5 text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed">
                <p>
                  Solapur Chaddar and Solapur Terry Towel carry Government of India
                  Geographical Indication tags — making them authenticated Indian
                  products with protected heritage status.
                </p>
                <p className="text-[var(--ovi-indigo)] font-medium">
                  OVI is the first digital marketplace built specifically for
                  GI-tagged Solapur textiles.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <GiTile
                  badge="GI Tag"
                  title="Solapur Chaddar"
                  body="Protected heritage status under the GI Act of India."
                />
                <GiTile
                  badge="GI Tag"
                  title="Solapur Terry Towel"
                  body="Recognised globally as an authentic Indian textile product."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-[var(--ovi-indigo)] text-[var(--ovi-cream-2)] ovi-noise">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <p className="text-xs uppercase tracking-[0.28em] text-[#E4B79A] font-semibold mb-5">
            Born in Solapur, Built for India.
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[var(--ovi-cream-2)] max-w-3xl mx-auto leading-[1.1]">
            Real textiles. Real makers. Directly to you.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3 sm:gap-4">
            <Button
              asChild
              className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-12 px-6 active:scale-[0.98] transition-[background-color,transform] duration-200"
            >
              <Link to="/makers" data-testid="about-cta-shop">
                Meet the makers
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-[var(--ovi-cream-2)]/40 bg-transparent text-[var(--ovi-cream-2)] hover:bg-[rgba(245,239,230,0.08)] hover:text-[var(--ovi-cream-2)] rounded-full h-12 px-6"
            >
              <Link to="/verified" data-testid="about-cta-makers">
                Partner with OVI
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

const GiTile = ({ badge, title, body }) => (
  <div className="bg-[var(--ovi-cream)] border border-[var(--ovi-border)] rounded-2xl p-5 sm:p-6">
    <div className="flex items-center gap-2">
      <Award className="h-4 w-4 text-[var(--ovi-terracotta)]" />
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--ovi-terracotta)]">
        {badge}
      </span>
    </div>
    <h3 className="mt-3 font-serif-display text-xl text-[var(--ovi-indigo)]">{title}</h3>
    <p className="mt-2 text-sm text-[var(--ovi-muted-ink)] leading-relaxed">{body}</p>
  </div>
);
